import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Row, Col } from 'reactstrap';
import { getCurrentDate } from '../../utils/DateUtils';
import { getEquipmentMeter } from '../../services/MaintenanceServices';
import TableRowsServiceSpareParts from './TableRowsServiceSpareParts';
import {
    RECORD_TYPES,
    SERVICE_TYPES,
    SERVICED_BY_OPTIONS,
} from './maintenanceServiceConstants';

function FileUploadField({
    fileKey,
    label,
    selectedFile,
    existingUrl,
    onSelect,
    onClear,
    readOnly,
    canClear,
}) {
    const displayName = selectedFile ? selectedFile.name : '';
    const hasFile = Boolean(displayName || existingUrl);

    return (
        <>
            <label className="form-group col-sm-4 text-right">{label}</label>
            <div className="col-sm-7 company-file-upload-row">
                <div className="company-file-upload company-file-upload-grow">
                    {!readOnly && (
                        <>
                            <input
                                type="file"
                                id={fileKey}
                                name={fileKey}
                                accept=".pdf,.jpg,.jpeg,.png,.webp"
                                onChange={onSelect}
                                className="company-file-upload-input"
                            />
                            <label htmlFor={fileKey} className="company-file-upload-btn">Choose File</label>
                        </>
                    )}
                    <span className={`company-file-upload-status ${hasFile ? 'has-file' : ''}`}>
                        {displayName || (existingUrl ? (
                            <a href={existingUrl} target="_blank" rel="noreferrer">View uploaded file</a>
                        ) : 'No file uploaded')}
                    </span>
                </div>
                {canClear && !readOnly && (
                    <div className="company-file-clear">
                        <button type="button" className="btn btn-link btn-sm" onClick={onClear}>Clear</button>
                    </div>
                )}
            </div>
        </>
    );
}

export default function MaintenanceServiceForm({
    cookies,
    plants,
    equipments,
    vendors,
    inputs,
    setInputs,
    spareParts,
    setSpareParts,
    selectedFiles,
    setSelectedFiles,
    clearFiles,
    setClearFiles,
    readOnly = false,
}) {
    const [meterInfo, setMeterInfo] = useState({ current_hm: 0, current_km: 0 });

    const filteredEquipments = useMemo(() => equipments, [equipments]);

    useEffect(() => {
        if (!inputs.equipment_id || !cookies) return;
        getEquipmentMeter(cookies, inputs.equipment_id)
            .then((response) => {
                setMeterInfo({
                    current_hm: response.current_hm || 0,
                    current_km: response.current_km || 0,
                });
            })
            .catch(() => setMeterInfo({ current_hm: 0, current_km: 0 }));
    }, [cookies, inputs.equipment_id]);

    useEffect(() => {
        const partsTotal = spareParts.reduce((sum, row) => sum + (Number(row.total_cost) || 0), 0);
        setInputs((prev) => {
            const labour = Number(prev.labour_cost) || 0;
            const nextParts = Number(partsTotal.toFixed(2));
            const nextTotal = Number((labour + partsTotal).toFixed(2));
            if (prev.parts_cost === nextParts && prev.service_cost === nextTotal) return prev;
            return { ...prev, parts_cost: nextParts, service_cost: nextTotal };
        });
    }, [spareParts, inputs.labour_cost, setInputs]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setInputs((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileSelect = (fileKey) => (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setSelectedFiles((prev) => ({ ...prev, [fileKey]: file }));
        if (fileKey === 'bill_file') setClearFiles((prev) => ({ ...prev, bill: false }));
        if (fileKey === 'service_report_file') setClearFiles((prev) => ({ ...prev, report: false }));
    };

    const isPreventive = inputs.record_type === 'PREVENTIVE';
    const isBreakdown = inputs.record_type === 'BREAKDOWN';
    const isVendorService = inputs.serviced_by === 'VENDOR';

    return (
        <div className="container form-control-panel">
            <Row>
                <Col xl={6} lg={12}>
                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                        <label className="form-group col-sm-4 text-right">Plant</label>
                        <select name="plant_id" className="form-control col-sm-7 mandatory-form-control" value={inputs.plant_id || ''} onChange={handleChange} required disabled={readOnly}>
                            <option value="">Select Plant</option>
                            {plants.map((p) => (<option key={p.value} value={p.value}>{p.label}</option>))}
                        </select>

                        <label className="form-group col-sm-4 text-right">Equipment</label>
                        <select name="equipment_id" className="form-control col-sm-7 mandatory-form-control" value={inputs.equipment_id || ''} onChange={handleChange} required disabled={readOnly}>
                            <option value="">Select Equipment</option>
                            {filteredEquipments.map((eq) => (
                                <option key={eq.id} value={eq.id}>{eq.equip_name} ({eq.equip_type?.name || 'Equipment'})</option>
                            ))}
                        </select>

                        {inputs.equipment_id && (
                            <>
                                <label className="form-group col-sm-4 text-right">Current HM / KM</label>
                                <input className="form-control col-sm-7" value={`HM ${meterInfo.current_hm} · KM ${meterInfo.current_km}`} readOnly />
                            </>
                        )}

                        <label className="form-group col-sm-4 text-right">Record Type</label>
                        <select name="record_type" className="form-control col-sm-7" value={inputs.record_type} onChange={handleChange} disabled={readOnly}>
                            {RECORD_TYPES.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                        </select>

                        <label className="form-group col-sm-4 text-right">Service Type</label>
                        <select name="service_type" className="form-control col-sm-7" value={inputs.service_type} onChange={handleChange} disabled={readOnly}>
                            {SERVICE_TYPES.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                        </select>

                        <label className="form-group col-sm-4 text-right">Service Title</label>
                        <input name="service_title" className="form-control col-sm-7" value={inputs.service_title || ''} onChange={handleChange} readOnly={readOnly} placeholder="e.g. Engine oil + filter change" />

                        <label className="form-group col-sm-4 text-right">Service Date</label>
                        <input type="date" className="form-control col-sm-7" value={inputs.service_date ? moment(inputs.service_date, ['DD-MM-YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD') : getCurrentDate()} onChange={(e) => setInputs((prev) => ({ ...prev, service_date: moment(e.target.value).format('DD-MM-YYYY') }))} readOnly={readOnly} required />

                        <label className="form-group col-sm-4 text-right">HM at Service</label>
                        <input type="number" step="0.001" name="service_hm" className="form-control col-sm-7" value={inputs.service_hm || 0} onChange={handleChange} readOnly={readOnly} />

                        <label className="form-group col-sm-4 text-right">KM at Service</label>
                        <input type="number" step="0.001" name="service_km" className="form-control col-sm-7" value={inputs.service_km || 0} onChange={handleChange} readOnly={readOnly} />

                        <label className="form-group col-sm-4 text-right">Prepared By</label>
                        <input name="prepared_by" className="form-control col-sm-7" value={inputs.prepared_by || ''} onChange={handleChange} readOnly={readOnly} />
                    </div>
                </Col>

                <Col xl={6} lg={12}>
                    {isPreventive && (
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                            <div className="col-sm-12 mb-2"><strong>Next Service Due (Alerts)</strong></div>
                            <label className="form-group col-sm-4 text-right">Due Date</label>
                            <input type="date" className="form-control col-sm-7" value={inputs.next_service_due_date ? moment(inputs.next_service_due_date, 'DD-MM-YYYY').format('YYYY-MM-DD') : ''} onChange={(e) => setInputs((prev) => ({ ...prev, next_service_due_date: e.target.value ? moment(e.target.value).format('DD-MM-YYYY') : '' }))} readOnly={readOnly} />
                            <label className="form-group col-sm-4 text-right">Due Hour Meter</label>
                            <input type="number" step="0.001" name="next_service_due_hm" className="form-control col-sm-7" value={inputs.next_service_due_hm || 0} onChange={handleChange} readOnly={readOnly} />
                            <label className="form-group col-sm-4 text-right">Due KM</label>
                            <input type="number" step="0.001" name="next_service_due_km" className="form-control col-sm-7" value={inputs.next_service_due_km || 0} onChange={handleChange} readOnly={readOnly} />
                            <div className="col-sm-12 text-muted small mt-1">Alerts when HM is within 50 hrs of due, or date is within 7 days / overdue.</div>
                        </div>
                    )}

                    {isBreakdown && (
                        <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                            <div className="col-sm-12 mb-2"><strong>Breakdown Details</strong></div>
                            <label className="form-group col-sm-4 text-right">Cause</label>
                            <input name="breakdown_cause" className="form-control col-sm-7 mandatory-form-control" value={inputs.breakdown_cause || ''} onChange={handleChange} readOnly={readOnly} />
                            <label className="form-group col-sm-4 text-right">Details</label>
                            <textarea name="breakdown_details" className="form-control col-sm-7" rows="4" value={inputs.breakdown_details || ''} onChange={handleChange} readOnly={readOnly} />
                        </div>
                    )}

                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary mb-3">
                        <div className="col-sm-12 mb-2"><strong>Service Provider & Cost</strong></div>
                        <label className="form-group col-sm-4 text-right">Serviced By</label>
                        <select name="serviced_by" className="form-control col-sm-7" value={inputs.serviced_by} onChange={handleChange} disabled={readOnly}>
                            {SERVICED_BY_OPTIONS.map((item) => (<option key={item.value} value={item.value}>{item.label}</option>))}
                        </select>

                        {isVendorService && (
                            <>
                                <label className="form-group col-sm-4 text-right">Vendor</label>
                                <select name="vendor_id" className="form-control col-sm-7" value={inputs.vendor_id || ''} onChange={handleChange} disabled={readOnly}>
                                    <option value="">Select Vendor</option>
                                    {vendors.map((v) => (<option key={v.id} value={v.id}>{v.entity_name || v.name}</option>))}
                                </select>
                                <label className="form-group col-sm-4 text-right">Vendor Name</label>
                                <input name="vendor_name" className="form-control col-sm-7" value={inputs.vendor_name || ''} onChange={handleChange} readOnly={readOnly} />
                            </>
                        )}

                        <label className="form-group col-sm-4 text-right">Labour Cost ₹</label>
                        <input type="number" step="0.01" name="labour_cost" className="form-control col-sm-7" value={inputs.labour_cost || 0} onChange={handleChange} readOnly={readOnly} />
                        <label className="form-group col-sm-4 text-right">Parts Cost ₹</label>
                        <input type="number" step="0.01" name="parts_cost" className="form-control col-sm-7" value={inputs.parts_cost || 0} readOnly />
                        <label className="form-group col-sm-4 text-right">Total Cost ₹</label>
                        <input type="number" step="0.01" name="service_cost" className="form-control col-sm-7" value={inputs.service_cost || 0} onChange={handleChange} readOnly={readOnly} />

                        <FileUploadField fileKey="bill_file" label="Bill Scan Copy" selectedFile={selectedFiles.bill_file} existingUrl={!clearFiles.bill ? inputs.bill_file_url : ''} onSelect={handleFileSelect('bill_file')} onClear={() => { setSelectedFiles((prev) => ({ ...prev, bill_file: null })); setClearFiles((prev) => ({ ...prev, bill: true })); setInputs((prev) => ({ ...prev, bill_file_url: '' })); }} readOnly={readOnly} canClear={Boolean(selectedFiles.bill_file || inputs.bill_file_url)} />
                        <FileUploadField fileKey="service_report_file" label="Service Report" selectedFile={selectedFiles.service_report_file} existingUrl={!clearFiles.report ? inputs.service_report_file_url : ''} onSelect={handleFileSelect('service_report_file')} onClear={() => { setSelectedFiles((prev) => ({ ...prev, service_report_file: null })); setClearFiles((prev) => ({ ...prev, report: true })); setInputs((prev) => ({ ...prev, service_report_file_url: '' })); }} readOnly={readOnly} canClear={Boolean(selectedFiles.service_report_file || inputs.service_report_file_url)} />

                        <label className="form-group col-sm-4 text-right">Remarks</label>
                        <textarea name="user_remarks" className="form-control col-sm-7" rows="2" value={inputs.user_remarks || ''} onChange={handleChange} readOnly={readOnly} />
                    </div>
                </Col>
            </Row>

            <div className="table-bordered shadow p-3 border-secondary mb-3">
                <h5 className="mb-3">Spare Parts Used</h5>
                <TableRowsServiceSpareParts rowsData={spareParts} setRowsData={setSpareParts} readOnly={readOnly} />
            </div>
        </div>
    );
}
